
import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText } from "lucide-react";

import API_BASE_URL from "../../api/api";

const API_URL =
  `${API_BASE_URL}/examination-cell-documents?limit=all`;

export default function ExaminationCellNoticesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [active, setActive] = useState(
    searchParams.get("tab") || ""
  );

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await fetch(API_URL);

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const result = await response.json();

        if (result.status && Array.isArray(result.data)) {
          const data = result.data.filter(
            (item) => Number(item.status) === 1
          );

          setDocuments(data);

          const tabs = [
            ...new Set(
              data.map((item) => item.event_type).filter(Boolean)
            ),
          ];

          const requestedTab = searchParams.get("tab");

          setActive(
            requestedTab && tabs.includes(requestedTab)
              ? requestedTab
              : tabs[0] || ""
          );
        } else {
          setDocuments([]);
        }
      } catch (error) {
        console.error("Examination Cell API error:", error);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const dynamicTabs = [
    ...new Set(
      documents.map((item) => item.event_type).filter(Boolean)
    ),
  ];

  const currentData = documents.filter(
    (item) => item.event_type === active
  );

  const formatEventType = (value) =>
    (value || "")
      .replace(/[-_]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const handleTabChange = (eventType) => {
    setActive(eventType);
    setSearchParams({ tab: eventType });
  };

  return (
    <section className="min-h-screen bg-[#f8fafc] py-12">
      <div className="max-w-7xl mx-auto px-6">
      
<button
  type="button"
  onClick={() => navigate("/examination-cell")}
  className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors"
>
  <ArrowLeft size={18} />
  Back
</button>

        <div className="text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-red-600">
            Examination Cell Notices
          </h1>
          <p className="mt-3 text-gray-600">
            All examination notices and documents
          </p>
        </div>

        {!loading && dynamicTabs.length > 0 && (
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {dynamicTabs.map((eventType) => (
              <button
                key={eventType}
                type="button"
                onClick={() => handleTabChange(eventType)}
                className={`px-5 py-3 rounded-xl border font-semibold ${
                  active === eventType
                    ? "bg-[#B5121B] text-white border-[#B5121B]"
                    : "bg-white text-[#24364b] border-gray-200"
                }`}
              >
                {formatEventType(eventType)}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 mb-5">
          <h2 className="text-xl font-bold text-[#08245c]">
            {formatEventType(active)}
          </h2>
          {!loading && (
            <p className="mt-1 text-sm text-gray-500">
              Total documents: {currentData.length}
            </p>
          )}
        </div>

        {loading && (
          <p className="text-center py-12 text-gray-500">
            Loading examination documents...
          </p>
        )}

        {!loading && currentData.length === 0 && (
          <p className="text-center py-12 text-gray-500">
            No documents available in this category.
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          {!loading &&
            currentData.map((item) => (
              <a
                key={item.id}
                href={item.pdf_path || item.external_link || undefined}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (!item.pdf_path && !item.external_link) {
                    e.preventDefault();
                  }
                }}
                className="flex items-center gap-4 bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-lg transition-all"
              >
                <FileText
                  size={25}
                  className="shrink-0 text-[#B5121B]"
                />

                <div className="flex-1 min-w-0">
                  <h3 className="text-[#24364b] font-semibold">
                    {item.heading}
                  </h3>

                  <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-500">
                    {item.publish_date && (
                      <span>{item.publish_date}</span>
                    )}
                    <span className="text-[#B5121B]">
                      {formatEventType(item.event_type)}
                    </span>
                  </div>
                </div>

                <ArrowUpRight
                  size={18}
                  className="shrink-0 text-[#B5121B]"
                />
              </a>
            ))}
        </div>
      </div>
    </section>
  );
}
